// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { DateTime } from 'luxon'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { post } from '../../helpers/apiHelper.ts'
import { ALL_FILTERS_ON, applyFiltersToEvents, categorizeEvents, MAX_FILTERS } from '../../helpers/eventHelper.tsx'
import { PET_EVENTS_URL } from '../../helpers/urls.ts'
import dataStore from '../../store/dataStore.ts'
import { IDevice, IEvent } from '../../interfaces/types.ts'
import { EventItem } from './EventItem.tsx'
import './Events.css'
import { EventOptions } from './EventOptions.tsx'
import { useLatest } from '../../hooks/useLatest.ts'

const EMPTY_EVENTS = {
    powerEvents: [],
    chargerEvents: [],
    beaconEvents: [],
    safeZoneEvents: [],
    commandEvents: [],
}

interface Props {
    selectedPetId: string
}

export const ScrollControl: React.FC<Props> = (props) => {
    const {selectedPetId} = props

    const [events, setEvents] = React.useState<IEvent[]>([])
    const [filteredEvents, setFilteredEvents] = React.useState<Record<string, IEvent[]>>(EMPTY_EVENTS)
    const [list, setList] = React.useState<React.ReactElement[]>([])
    const [filters, setFilters] = React.useState<string[]>(ALL_FILTERS_ON)
    const [filtersChanged, setFiltersChanged] = React.useState(false)
    const [readAll, setReadAll] = React.useState(false)

    // Derive date range from events — no state, no fighting effects.
    const startDate = events.length ? DateTime.fromISO(events[0].createdAt) : DateTime.now()
    const endDate = events.length ? DateTime.fromISO(events[events.length - 1].createdAt) : DateTime.now()

    // Use refs for values accessed in callbacks to avoid stale closures and unnecessary callback recreation
    const isLoadingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false); // Only for rendering the loader indicator
    const devices = dataStore(s => s.devices)
    const devicesRef = useLatest(devices);
    const loaderRef = useRef(null);

    // Refs for IntersectionObserver callback to read current values without re-setting up the observer
    const eventsRef = useLatest(events);
    const readAllRef = useLatest(readAll);
    const filtersRef = useLatest(filters);
    const selectedPetIdRef = useLatest(selectedPetId);

    const getEvents = useCallback(async (petId: string, start: string, direction: 'forward' | 'back') => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        const device: IDevice | undefined = devicesRef.current.find(d => d.petId === petId)
        try {
            const res = (await post(PET_EVENTS_URL, {
                petId,
                iccid: device?.iccid,
                startDate: start,
                direction,
            }))!
            if (res.status === 200) {
                if (res.data.length) {
                    // Use functional updater to avoid stale closure on events
                    setEvents(prev => prev.concat(res.data))
                } else {
                    setReadAll(true)
                }
            }
        } catch (e) {
            console.log('getHistory error', e)
        }
        isLoadingRef.current = false;
        setIsLoading(false)
    }, []) // No deps needed - refs handle stale values

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const target = entries[0];
            if (target.isIntersecting) {
                const currentEvents = eventsRef.current;
                if (!readAllRef.current && currentEvents.length && filtersRef.current.length > 1) {
                    // console.log('getEvents', currentEvents.length)
                    const last = currentEvents.length - 1
                    const iso = DateTime.fromISO(currentEvents[last].createdAt).toISO()!
                    void getEvents(selectedPetIdRef.current, iso, 'back')
                }
            }
        });

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => {
            if (loaderRef.current) {
                observer.unobserve(loaderRef.current);
            }
        };
    }, [getEvents]);

    React.useEffect(() => {
        if (selectedPetId) {
            setEvents([])
            setReadAll(false)
            const iso = DateTime.now().toISO()!
            void getEvents(selectedPetId, iso, 'back')
        }
    }, [selectedPetId])

    React.useEffect(() => {
        if (events) {
            const update= categorizeEvents(filters, events)
            const didUpdate = didFilteredListChange(filters, filteredEvents, update)
            if (filtersChanged && !didUpdate) {
                doRender()
            } else if (didUpdate) {
                doRender()
            }
            if (filtersChanged) setFiltersChanged(false)
            setFilteredEvents(update)
        }
    }, [events, filters])

    const didFilteredListChange = (filters: string[], prev: Record<string, IEvent[]>, next: Record<string, IEvent[]>) => {
        if (filters.length === MAX_FILTERS) return true
        let v = false;
        filters.forEach(f => {
            if (prev[f].length !== next[f].length) {
                v = true
            }
        })
        return v
    }

    const renderFilteredEvents = () => {
        const ev: IEvent[] = applyFiltersToEvents(filters, events)
        return ev.map((event: IEvent, i: number) => {
            return <EventItem key={`${event._id}-${i}`} event={event} index={i}/>
        })
    }

    const doRender = () => {
        const list = renderFilteredEvents()
        setList(list)
    }

    const loader = <span className="loading loading-bars loading-md"></span>

    return (
        <div className={'flex flex-col flex-1 min-h-0'}>
            <EventOptions setFilters={setFilters} setFiltersChanged={setFiltersChanged} startDate={startDate} endDate={endDate} events={events}/>
            <div id={'content'} className={'scrollable p-1 flex-1 min-h-0'}>
                {list}
                <div key={'load-trigger'} id={'load-trigger'} ref={loaderRef}>{isLoading && loader}</div>
            </div>
        </div>
    )
}
